import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { PdfTemplateType } from "../document-template.types";
import { getConfig } from "../../context/util";
import clsx from "clsx";
import { formatAddressLines } from "@/dentlab/src/lib/utils/address-formatter";
import { useCentralStore } from "../../store/Central";
import { countryLookup } from "../../lib/constants/countries";
import { AddressRecipient } from "../utils";

export const PdfRecipientAddress: React.FC<{
	recipient: AddressRecipient;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	template?: PdfTemplateType | "general";
}> = ({ recipient, pdfTemplateConfiguration, template }) => {
	const organization = useCentralStore((state) => state.organization);
	const alignment = {
		left: "start",
		center: "center",
		right: "end",
	};
	// const config = pdfTemplateConfiguration?.[template ?? "general"];
	const generalConfig = pdfTemplateConfiguration!.general;
	const pdfConfig = pdfTemplateConfiguration?.[template ?? "general"] || {};

	const config = getConfig(pdfConfig, generalConfig);

	const fontSize = {
		sm: "7pt",
		md: "8pt",
		lg: "10pt",
		xl: "11pt",
		xxl: "12pt",
	};

	const height = {
		sm: "16mm",
		md: "20mm",
		lg: "25mm",
	};

	const padding = {
		sm: "1mm",
		md: "5mm",
		lg: "10mm",
	};

	const isClientWithCorrespondence = recipient?.is_correspondence_address;

	const isCompany = isClientWithCorrespondence
		? recipient?.c_is_company
		: recipient?.is_company;

	const addressData = isClientWithCorrespondence
		? {
				title: recipient?.c_title ?? "",
				firstName: recipient?.c_first_name ?? "",
				lastName: recipient?.c_last_name ?? "",
				street: recipient?.c_street ?? "",
				postalCode: recipient?.c_postal_code ?? "",
				city: recipient?.c_city ?? "",
				country: recipient?.c_country ?? "",
				companyName: recipient?.c_company_name ?? "",
				additionalInformation:
					recipient?.c_additional_information ?? "",
			}
		: {
				title: recipient?.title ?? "",
				firstName: recipient?.first_name ?? "",
				lastName: recipient?.last_name ?? "",
				street: recipient?.street ?? "",
				postalCode: recipient?.postal_code ?? "",
				city: recipient?.city ?? "",
				country: recipient?.country ?? "",
				companyName: recipient?.company_name ?? "",
				additionalInformation: recipient?.additional_information ?? "",
			};

	const recipientCountry = addressData.country.toLowerCase();
	const labCountry = organization?.country?.toLowerCase() ?? "";
	const showCountry =
		recipientCountry.length > 0 && recipientCountry !== labCountry;
	const countryLabel = countryLookup[recipientCountry] ?? addressData.country;

	const formattedAddress = formatAddressLines(isCompany ?? false, {
		title: addressData.title,
		firstName: addressData.firstName,
		lastName: addressData.lastName,
		companyName: addressData.companyName,
	});

	const addressFirstLine = formattedAddress.firstLine;
	const addressSecondLine = formattedAddress.secondLine;

	// Check if sender address should be above recipient
	const showSenderAboveRecipient =
		generalConfig.senderAddressAboveRecipient && organization;

	return (
		<div
			aria-label="Empfängeradresse"
			style={{
				height: height[generalConfig.size ?? "lg"],
				paddingBottom: padding[generalConfig.size ?? "lg"],
				width: "100%",
				paddingLeft: generalConfig?.clientAddressLeft ? "6mm" : "97mm", // Linksadressierungen beginnen 26mm von links, Rechtsadressierungen 117mm von links.
				fontSize:
					fontSize[
						config.recipientAddressFontSize ??
							generalConfig.size ??
							"lg"
					],
				display: "flex",
				...(!config.absoluteRecipientAddress
					? {
							alignItems:
								alignment[
									config.recipientAddressAlignment ?? "left"
								],
						}
					: {}),
				flexDirection: "column",
				...(generalConfig.recipientAddressTopGap !== undefined && {
					paddingTop: `${generalConfig.recipientAddressTopGap}mm`,
				}),
				...(generalConfig.recipientAddressRightGap !== undefined && {
					paddingRight: `${generalConfig.recipientAddressRightGap}mm`,
				}),
			}}
		>
			<div
				className={clsx("text-left", {
					"top-[48mm] absolute": config.absoluteRecipientAddress,
					"left-[120mm]":
						config.absoluteRecipientAddress &&
						config.recipientAddressAlignment === "right",
					"left-[20mm]":
						config.absoluteRecipientAddress &&
						config.recipientAddressAlignment !== "right",
				})}
			>
				{showSenderAboveRecipient && (
					<div
						style={{
							fontSize:
								fontSize[
									config.senderAddressFontSize ??
										generalConfig.size ??
										"lg"
								],
							marginBottom: "2mm",
						}}
					>
						<p className="flex text-[7pt] underline items-center whitespace-nowrap gap-0.5">
							{organization.title_or_company_name ||
								(organization.name && (
									<>
										{organization.title_or_company_name ||
											organization.name}
										{", "}
									</>
								))}
							{", "}
							{organization.name_or_additional_information && (
								<>
									{
										organization.name_or_additional_information
									}
									{", "}
								</>
							)}
							{organization.street && (
								<>
									{organization.street}
									{", "}
								</>
							)}
							{organization.postal_code && (
								<>{organization.postal_code + " "}</>
							)}
							{organization.city && <>{organization.city}</>}
						</p>
					</div>
				)}
				{addressFirstLine && <p>{addressFirstLine}</p>}
				{addressSecondLine && <p>{addressSecondLine}</p>}
				{addressData.additionalInformation && (
					<p>{addressData.additionalInformation}</p>
				)}
				<p>{addressData.street}</p>
				<p>
					{addressData.postalCode} {addressData.city}
				</p>
				{showCountry && <p>{countryLabel}</p>}
			</div>
		</div>
	);
};
